import { Typography } from '@material-ui/core';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { CtaButton } from '../CtaButton';
import { Headline } from '../Headline';
import { Kicker } from '../Kicker';
import { mobileHeroLeadText } from '../MarketingTypography';
import { LogosSection } from '../LogosSection';
import { Reveal } from '../motion';
import { AgenticMontage } from './AgenticMontage';
import { HeroGradientBackground } from './GradientBackground';

const HeroSectionWrapper = styled('section')`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const HeroContentWrapper = styled('div')`
  position: relative;
  overflow: hidden;
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 620px;
  padding: 80px 0 112px;

  @media (max-width: 960px) {
    min-height: auto;
    padding: 48px 0 56px;
  }
`;

const HeroWrapper = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 48px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 32px 24px 32px;
  }
`;

const LeftContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 600px) {
    gap: 16px;
  }
`;

const Description = styled(Typography)`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
  ${mobileHeroLeadText}

  strong {
    color: #0e1c2b;
  }
` as typeof Typography;

const ButtonGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LearnMoreLink = styled('a')`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const HeroHeadline = styled(Headline)`
  &.MuiTypography-h1 {
    font-size: 3.4rem;

    @media (max-width: 960px) {
      font-size: 2.6rem;
    }

    @media (max-width: 600px) {
      font-size: 2.125rem;
    }

    @media (max-width: 400px) {
      font-size: 2rem;
    }
  }
`;

const RightContent = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeroSection = ({
  onGetStarted,
  showLogos = false
}: {
  onGetStarted: () => void;
  showLogos?: boolean;
}) => {
  return (
    <HeroSectionWrapper>
      <HeroContentWrapper>
        <HeroGradientBackground />
        <HeroWrapper>
          <LeftContent>
            <Reveal>
              <HeroHeadline component="h1">
                {
                  "Your content is valuable inventory. Make every page earn what\u00A0it's\u00A0worth."
                }
              </HeroHeadline>
              <Kicker style={{ marginTop: 12, marginBottom: 0 }}>
                AI commerce infrastructure for content publishers
              </Kicker>
            </Reveal>

            <Reveal delay={200}>
              <Description component="p">
                One platform to unify affiliate data, optimize performance,
                launch sponsorship campaigns, and maximize yield from every
                placement.
              </Description>
            </Reveal>

            <Reveal delay={300}>
              <ButtonGroup>
                <CtaButton size="medium" onClick={onGetStarted}>
                  Get a demo
                </CtaButton>
                <LearnMoreLink href="/product/amplify/">
                  Explore sponsorship campaigns <ArrowRight size={18} />
                </LearnMoreLink>
              </ButtonGroup>
            </Reveal>
          </LeftContent>

          <RightContent>
            <Reveal delay={250} distance={32}>
              <AgenticMontage />
            </Reveal>
          </RightContent>
        </HeroWrapper>
      </HeroContentWrapper>
      {showLogos ? <LogosSection variant="hero" /> : null}
    </HeroSectionWrapper>
  );
};
